// Implements: F-RPT-007
/**
 * Mockup v3 P9: the window "Actividad del mes" compares with ("+9 frente a septiembre"). Pure.
 *
 * A window that starts on the first of a month compares with the same days of the month before,
 * from its first day: the month so far (1-17 October) with 1-17 September, a whole month with the
 * whole month before (its last day clamped to that month's length, so 1-31 March reads 1-28
 * February, or 1-29 in a leap year). Any other window compares with the same number of days
 * immediately before it. Days are `YYYY-MM-DD` calendar dates, counted in UTC so no browser zone
 * moves them.
 */

import type { ReportWindow } from "../../../types/reports";

const DAY_MS = 86_400_000;

function isoDay(year: number, month: number, day: number): string {
  return new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);
}

function parts(day: string): [number, number, number] {
  const [year = 0, month = 0, date = 0] = day.split("-").map(Number);
  return [year, month, date];
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function previousWindow(window: ReportWindow): ReportWindow {
  const [fromYear, fromMonth, fromDate] = parts(window.from);
  const [toYear, toMonth, toDate] = parts(window.to);
  if (fromDate === 1 && fromYear === toYear && fromMonth === toMonth) {
    const year = fromMonth === 1 ? fromYear - 1 : fromYear;
    const month = fromMonth === 1 ? 12 : fromMonth - 1;
    const last = toDate === daysInMonth(toYear, toMonth) ? daysInMonth(year, month) : Math.min(toDate, daysInMonth(year, month));
    return { from: isoDay(year, month, 1), to: isoDay(year, month, last) };
  }
  const from = Date.UTC(fromYear, fromMonth - 1, fromDate);
  const length = Math.round((Date.UTC(toYear, toMonth - 1, toDate) - from) / DAY_MS) + 1;
  const start = new Date(from - length * DAY_MS);
  const end = new Date(from - DAY_MS);
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}

/** The calendar month (1-12) a window starts in; the screen names it in its own words. */
export function monthOf(window: ReportWindow): number {
  return parts(window.from)[1];
}

/**
 * The whole calendar month before the one a window starts in, from its first day to its last
 * (owner 2026-10-19: "Septiembre completo"): 1-19 October reads 1-30 September, any March reads
 * February to its 28th or, in a leap year, its 29th, and January reads the December before.
 */
export function wholeMonthBefore(window: ReportWindow): ReportWindow {
  const [fromYear, fromMonth] = parts(window.from);
  const year = fromMonth === 1 ? fromYear - 1 : fromYear;
  const month = fromMonth === 1 ? 12 : fromMonth - 1;
  return { from: isoDay(year, month, 1), to: isoDay(year, month, daysInMonth(year, month)) };
}

/** A word at the start of a line ("Septiembre completo"): its first letter upper case. */
export function capitalised(word: string): string {
  return word.charAt(0).toLocaleUpperCase("es") + word.slice(1);
}
